import { businessMatchesLocation, getBusinessLocationLabel, getGuineaLocationLabel } from '../constants/guineaLocations';

/**
 * Helpers de recherche d'entreprises (préfixe de nom, accents ignorés).
 */

export function normalizeSearchText(value) {
  if (value == null) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function getBusinessDisplayName(business) {
  return (business?.name || business?.businessName || '').trim();
}

const SMALL_WORDS = new Set([
  'le', 'la', 'les', 'de', 'du', 'des', 'et', 'a', 'un', 'une', 'the', 'of', 'chez', 'au', 'aux'
]);

function namePrefixMatches(name, query) {
  if (!name || !query) return false;
  if (name.startsWith(query)) return true;
  const parts = name.split(/[\s,;:_\-\/|]+/).filter(Boolean);
  return parts.some((part) => {
    const cleaned = part.replace(/^l['’]/, '');
    if (query.length === 1 && SMALL_WORDS.has(cleaned)) return false;
    return cleaned.startsWith(query);
  });
}

/**
 * Score de pertinence : noms qui commencent par la requête d'abord.
 * 100 = préfixe exact du nom, 50 = nom contient, 25 = autre champ, 0 = pas de match.
 */
export function scoreBusinessMatch(business, rawQuery, { categories = [], isAdmin = false } = {}) {
  const query = normalizeSearchText(rawQuery);
  if (!query) return 0;

  const name = normalizeSearchText(getBusinessDisplayName(business));
  if (namePrefixMatches(name, query)) return 100;

  // Une seule lettre : uniquement les noms qui commencent par cette lettre
  if (query.length === 1) return 0;

  if (name.includes(query)) return 50;

  const address = normalizeSearchText(business?.address);
  if (address.includes(query)) return 25;

  const location = normalizeSearchText(
    `${getBusinessLocationLabel(business)} ${getGuineaLocationLabel(business?.quartier)} ${business?.quartier || ''}`
  );
  if (location.includes(query)) return 25;

  const description = normalizeSearchText(business?.description);
  if (description.includes(query)) return 25;

  if (Array.isArray(business?.services)) {
    if (business.services.some((service) => normalizeSearchText(service).includes(query))) {
      return 25;
    }
  }

  if (business?.phone && String(business.phone).includes(rawQuery.trim())) {
    return 25;
  }

  if (isAdmin && normalizeSearchText(business?.email).includes(query)) {
    return 25;
  }

  const category = categories.find((cat) => cat.id === business?.category);
  if (category && normalizeSearchText(category.name).includes(query)) {
    return 25;
  }

  // Catégorie stockée parfois en slug texte
  if (normalizeSearchText(business?.category).includes(query)) {
    return 25;
  }

  return 0;
}

export function filterBusinessesByQuery(businesses, rawQuery, options = {}) {
  const query = normalizeSearchText(rawQuery);
  if (!query) return [...(businesses || [])];

  return (businesses || [])
    .map((business) => ({
      business,
      score: scoreBusinessMatch(business, rawQuery, options)
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return getBusinessDisplayName(a.business).localeCompare(
        getBusinessDisplayName(b.business),
        'fr',
        { sensitivity: 'base' }
      );
    })
    .map(({ business }) => business);
}

export function filterBusinessesByLocation(businesses, locationKey) {
  if (!locationKey) return [...(businesses || [])];
  return (businesses || []).filter((business) => businessMatchesLocation(business, locationKey));
}
